import React, { useState } from 'react';
import {
  AlertCircle, CheckCircle2, CircleDot, Clock3, Download, Globe, Loader2, Monitor, Smartphone, Workflow, XCircle,
} from 'lucide-react';

import { loadReportDownload } from './api';
import { tx } from './i18n';
import { lengthOf } from './MobileParts';
import { Pill } from './parts';
import { errorMessage } from './ui';
import './reporting.css';

// What the Reporting section's inventory, analytics and file pages share: how a
// report's source, status and failure reason are named, how sizes and times read,
// and the one way a report file is opened.

export const text = (key, values) => tx(`ui.reports.library.${key}`, values);

export const SOURCES = ['portal', 'app', 'pipeline', 'webserver', 'unknown'];
const SOURCE_ICONS = { portal: Monitor, app: Smartphone, pipeline: Workflow, webserver: Globe, unknown: CircleDot };

export function sourceLabel(code) {
  return text(`source_${code}`);
}

// Which service asked for a report. Compact, it is the mark alone, named on hover.
export function SourceMark({ source, compact = false }) {
  const Icon = SOURCE_ICONS[source] || CircleDot;
  const label = sourceLabel(source);
  if (compact) {
    return <span className={`xo-rep-source is-${source}`} role="img" aria-label={label} title={label}><Icon aria-hidden="true" /></span>;
  }
  return <span className={`xo-rep-source is-${source}`}><Icon aria-hidden="true" />{label}</span>;
}

export const STATUS = {
  queued: { key: 'ui.reports.status_queued', tone: 'warn', Icon: Clock3 },
  processing: { key: 'ui.reports.status_processing', tone: 'info', Icon: Loader2 },
  done: { key: 'ui.reports.status_ready', tone: 'ok', Icon: CheckCircle2 },
  failed: { key: 'ui.reports.status_failed', tone: 'bad', Icon: AlertCircle },
  cancelled: { key: 'ui.reports.library.status_cancelled', tone: 'flat', Icon: XCircle },
};

export function statusLabel(status) {
  return tx((STATUS[status] || STATUS.cancelled).key);
}

export function StatusPill({ status }) {
  const meta = STATUS[status] || STATUS.cancelled;
  return <Pill tone={meta.tone}>{tx(meta.key)}</Pill>;
}

export function reasonLabel(code) {
  return text(`reason_${code}`);
}

const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
const byteFormats = new Map();

export function bytesLabel(bytes) {
  if (bytes === null || bytes === undefined) return text('not_recorded');
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) { value /= 1024; unit += 1; }
  if (!byteFormats.has(unit)) {
    byteFormats.set(unit, new Intl.NumberFormat(undefined, {
      style: 'unit', unit: BYTE_UNITS[unit], unitDisplay: 'short', maximumFractionDigits: unit === 0 ? 0 : 1,
    }));
  }
  return byteFormats.get(unit).format(value);
}

const SECONDS = new Intl.NumberFormat(undefined, { style: 'unit', unit: 'second', unitDisplay: 'short', maximumFractionDigits: 1 });

// Generation takes seconds, so under a minute a tenth of a second still says something.
export function durationLabel(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return text('not_recorded');
  if (ms < 60000) return SECONDS.format(ms / 1000);
  return lengthOf(ms);
}

export function dateLabel(at) {
  if (at === null || at === undefined) return text('not_recorded');
  return new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// Opens a report's PDF in a new tab through the report's own download route,
// which decides who may open it and signs a five-minute link. These pages never
// hold a storage key's signature themselves. The tab is opened before the link
// is asked for, while the click still counts as the person's own.
export async function openReportFile(uuid) {
  const tab = window.open('', '_blank');
  if (tab !== null) tab.opener = null;
  try {
    const result = await loadReportDownload(uuid);
    if (tab !== null) tab.location.replace(result.url);
    else {
      const link = document.createElement('a');
      link.href = result.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }
  } catch (caught) {
    if (tab !== null) tab.close();
    throw caught;
  }
}

export function DownloadReport({ uuid, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function open(event) {
    event.stopPropagation();
    setBusy(true);
    setError('');
    try {
      await openReportFile(uuid);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="xo-rep-download">
      <button type="button" className="xo-btn-2" onClick={open} disabled={busy}
        aria-label={text('download')} title={text('download_hint')}>
        {busy ? <Loader2 className="xo-spin" aria-hidden="true" /> : <Download aria-hidden="true" />}
        {!compact && <span>{text('download')}</span>}
      </button>
      {error && <small className="t-bad" role="alert">{error}</small>}
    </span>
  );
}
